import React, { FormEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useSearchParams } from 'react-router-dom';
import { getBrand } from '../../constants/brands';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  EmptyState,
  Input,
  LoadingBlock,
  PageContainer,
  PageHeader,
  Stack,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '../../components/ui';
import { useCardholderNameNoticesQuery } from '../../services/cardholderNameService/__generated__/queries.generated';
import {
  cardText,
  comparedWithText,
  notifiedText,
  purposeText,
  tripDatesText,
  tripIdFilter,
  withTripIdFilter,
} from './cardNameChecks';

const LIMIT = 200;

const Filters = styled.form`
  ${tw`flex flex-wrap items-end gap-3 p-4`}
`;
const FilterInput = styled.div`
  ${tw`flex-1`}
  min-width: 240px;
`;
const FilterButton = styled.div`
  ${tw`flex-shrink-0`}
`;
const Cell = styled.div`
  ${tw`flex flex-col min-w-0`}
`;
const Sub = styled.span`
  ${tw`text-xs`}
  color: var(--color-text-secondary);
`;
const TripId = styled.code`
  ${tw`text-xs break-all`}
  user-select: all;
`;

/**
 * Spec 013 P7 (FR-036): cards in another name that this site accepted while
 * its cardholder name check was off, newest first. A brand admin sees this
 * brand's trips; the platform owner sees the request brand's. `?bookingId=`
 * (the link in the admin's email and notification) filters to one trip.
 */
export function AdminCardNameChecksPage() {
  const brand = getBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const tripId = tripIdFilter(searchParams);
  const [draft, setDraft] = useState(tripId ?? '');

  useEffect(() => {
    setDraft(tripId ?? '');
  }, [tripId]);

  const { data, loading, error } = useCardholderNameNoticesQuery({
    variables: { bookingId: tripId, limit: LIMIT },
    fetchPolicy: 'cache-and-network',
  });
  const rows = data?.cardholderNameNotices ?? [];

  const applyFilter = (value: string) => {
    setSearchParams(withTripIdFilter(searchParams, value));
  };
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    applyFilter(draft);
  };

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Admin"
        title="Card name checks"
        description={`Cards in another name than the guest's that ${brand.name} accepted while the cardholder name check was off. The host and brand admins were told when each card paid.`}
      />
      <Stack>
        {error && <Alert tone="error">{error.message}</Alert>}
        <Card>
          <Filters onSubmit={onSubmit} data-testid="card-name-checks-filter">
            <FilterInput>
              <Input
                aria-label="Trip ID"
                placeholder="Trip ID"
                data-testid="card-name-checks-trip-id"
                value={draft}
                onChange={event => setDraft(event.target.value)}
              />
            </FilterInput>
            <FilterButton>
              <Button theme="filled" size="md" type="submit" text="Filter" />
            </FilterButton>
            {tripId && (
              <FilterButton>
                <Button
                  theme="secondary"
                  size="md"
                  text="Clear"
                  onClick={() => applyFilter('')}
                  dataTestId="card-name-checks-clear"
                />
              </FilterButton>
            )}
          </Filters>
          {loading && !data && <LoadingBlock label="Loading card name checks" />}
          {data && rows.length === 0 && (
            <EmptyState
              compact
              title="No cards in another name were accepted on this site."
              description={tripId ? `Trip ${tripId}` : undefined}
            />
          )}
          {rows.length > 0 && (
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>Trip ID</TH>
                    <TH>Listing</TH>
                    <TH>Trip dates</TH>
                    <TH>Guest</TH>
                    <TH>Host</TH>
                    <TH>Name on card</TH>
                    <TH>Compared with</TH>
                    <TH>Paid for</TH>
                    <TH>Notified</TH>
                  </TR>
                </THead>
                <TBody>
                  {rows.map(row => (
                    <TR key={row.id} data-testid={`card-name-check-${row.id}`}>
                      <TD>
                        <TripId>{row.bookingId}</TripId>
                      </TD>
                      <TD>{row.listingName || 'Unknown listing'}</TD>
                      <TD>{tripDatesText(row.tripStartAt, row.tripEndAt, row.timezone)}</TD>
                      <TD>
                        <Cell>
                          <span>{row.guestName || 'No name'}</span>
                          {row.guestEmail && <Sub>{row.guestEmail}</Sub>}
                        </Cell>
                      </TD>
                      <TD>{row.hostName || 'No name'}</TD>
                      <TD>{cardText(row.cardholderName, row.cardLast4)}</TD>
                      <TD>{comparedWithText(row.accountName, row.accountNameSource)}</TD>
                      <TD $nowrap>{purposeText(row.purpose)}</TD>
                      <TD $nowrap>{notifiedText(row.notifiedAt)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </Card>
      </Stack>
    </PageContainer>
  );
}

export default AdminCardNameChecksPage;
